import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ArrowRight, ChevronLeft, ChevronDown, ChevronUp, Info, AlertTriangle, Wallet, DollarSign, Home, ShoppingCart, CreditCard, PiggyBank, Smile, Shield, Fuel, Wrench, Scale, Percent, Calculator, Wind, Umbrella } from 'lucide-react';

const BRAND = {
  navy: '#001F3F',
  red: '#FF4136',
  green: '#10B981',
  purple: '#8B5CF6',
  amber: '#F59E0B',
};

const SLIDER_CONFIG = {
  monthlyIncome: { min: 2000, max: 30000, step: 100 },
  housing: { min: 0, max: 10000, step: 50 },
  essentials: { min: 0, max: 3000, step: 50 },
  debtPayments: { min: 0, max: 3000, step: 50 },
  savingsGoal: { min: 0, max: 3000, step: 50 },
  wants: { min: 0, max: 3000, step: 50 },
  insurance: { min: 0, max: 800, step: 10 },
  fuel: { min: 0, max: 600, step: 10 },
  maintenance: { min: 0, max: 400, step: 10 },
};

const RISK_CONFIG = {
  conservative: { incomePct: 0.10, bufferPct: 0.08, label: 'Conservative' },
  balanced: { incomePct: 0.12, bufferPct: 0.05, label: 'Balanced' },
  aggressive: { incomePct: 0.15, bufferPct: 0.02, label: 'Aggressive' },
};

const FIELD_ICONS = {
  monthlyIncome: DollarSign,
  housing: Home,
  essentials: ShoppingCart,
  debtPayments: CreditCard,
  savingsGoal: PiggyBank,
  wants: Smile,
  insurance: Shield,
  fuel: Fuel,
  maintenance: Wrench,
};

const FIELD_LABELS = {
  monthlyIncome: 'Monthly take-home income',
  housing: 'Housing',
  essentials: 'Essentials',
  debtPayments: 'Debt payments',
  savingsGoal: 'Savings',
  wants: 'Fun money',
  insurance: 'Insurance',
  fuel: 'Fuel',
  maintenance: 'Maintenance',
};

const FIELD_HELPERS = {
  monthlyIncome: 'After taxes',
  housing: 'Rent, mortgage, HOA',
  essentials: 'Groceries, utilities, phone, childcare',
  debtPayments: 'Credit cards, student loans, personal loans',
  savingsGoal: 'Monthly savings goal',
  wants: 'Dining out, subscriptions, hobbies',
  insurance: 'Estimated monthly',
  fuel: 'Estimated monthly',
  maintenance: 'Oil, tires, repairs averaged',
};

const FIELD_TOOLTIPS = {
  monthlyIncome: 'Net income is the money that actually hits your bank account after taxes and deductions.',
};

const FIELD_PLACEHOLDERS = {
  insurance: 'Full coverage often runs ~$140–$225/mo depending on driver, vehicle, and state.',
  fuel: '~$150–$200/mo at 12k miles/year',
  maintenance: '~$60–$100/mo averaged (tires/oil/repairs)',
};

// Icon background gradients for each section
const ICON_BACKGROUNDS = {
  income: { bg: 'linear-gradient(135deg, #001F3F, #1E3A8A)', color: '#FFFFFF' },
  fixed: { bg: 'linear-gradient(135deg, #1F2937, #4B5563)', color: '#FFFFFF' },
  goals: { bg: 'linear-gradient(135deg, #001F3F, #1E3A8A)', color: '#FFFFFF' },
  car: { bg: 'linear-gradient(135deg, #CC2200, #FF4136)', color: '#FFFFFF' },
};

// Premium Gauge Component with animation
const BudgetGauge = ({ size = 'desktop' }) => {
  const [needleAngle, setNeedleAngle] = useState(-90);
  
  useEffect(() => {
    // Animate needle on load
    const timer = setTimeout(() => {
      setNeedleAngle(-45); // Point to green zone
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const dimensions = size === 'mobile' 
    ? { width: 150, height: 92, viewBox: '0 0 130 80' }
    : { width: 180, height: 108, viewBox: '0 0 130 80' };

  return (
    <div style={{ 
      position: 'relative', 
      width: dimensions.width, 
      height: dimensions.height,
      filter: 'drop-shadow(0 4px 12px rgba(0,31,63,0.12))',
    }}>
      <svg viewBox={dimensions.viewBox} style={{ width: '100%', height: '100%' }}>
        <defs>
          {/* Gradient for the arc */}
          <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#001F3F" />
            <stop offset="50%" stopColor="#E5E7EB" />
            <stop offset="100%" stopColor="#FF4136" />
          </linearGradient>
          {/* Shadow filter */}
          <filter id="gaugeShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15"/>
          </filter>
        </defs>
        
        {/* Background arc with shadow */}
        <path
          d="M 15 70 A 50 50 0 0 1 115 70"
          fill="none"
          stroke="#E8EAED"
          strokeWidth="10"
          strokeLinecap="round"
          filter="url(#gaugeShadow)"
        />
        
        {/* Navy segment (safe zone - left) */}
        <path
          d="M 15 70 A 50 50 0 0 1 45 25"
          fill="none"
          stroke="#001F3F"
          strokeWidth="10"
          strokeLinecap="round"
          style={{ filter: 'drop-shadow(0 1px 3px rgba(0,31,63,0.3))' }}
        />
        
        {/* Light Gray segment (middle zone) */}
        <path
          d="M 47 23 A 50 50 0 0 1 83 23"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="10"
          strokeLinecap="round"
          style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.1))' }}
        />
        
        {/* Red segment (danger zone - right) */}
        <path
          d="M 85 25 A 50 50 0 0 1 115 70"
          fill="none"
          stroke="#FF4136"
          strokeWidth="10"
          strokeLinecap="round"
          style={{ filter: 'drop-shadow(0 1px 3px rgba(255,65,54,0.3))' }}
        />
        
        {/* Tick marks */}
        {[0, 45, 90, 135, 180].map((angle, i) => {
          const rad = (angle - 180) * Math.PI / 180;
          const x1 = 65 + 42 * Math.cos(rad);
          const y1 = 70 + 42 * Math.sin(rad);
          const x2 = 65 + 48 * Math.cos(rad);
          const y2 = 70 + 48 * Math.sin(rad);
          return (
            <line
              key={i}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="#CBD5E1"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          );
        })}
        
        {/* Animated Needle */}
        <g 
          style={{ 
            transformOrigin: '65px 70px',
            transform: `rotate(${needleAngle}deg)`,
            transition: 'transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          {/* Needle shadow */}
          <line
            x1="65"
            y1="70"
            x2="65"
            y2="28"
            stroke="rgba(0,31,63,0.2)"
            strokeWidth="3"
            strokeLinecap="round"
            transform="translate(1, 1)"
          />
          {/* Needle */}
          <line
            x1="65"
            y1="70"
            x2="65"
            y2="28"
            stroke={BRAND.navy}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </g>
        
        {/* Center hub with layered effect */}
        <circle cx="65" cy="70" r="10" fill="#F1F5F9" />
        <circle cx="65" cy="70" r="8" fill={BRAND.navy} />
        <circle cx="65" cy="70" r="4" fill="#FFFFFF" />
        <circle cx="65" cy="70" r="2" fill={BRAND.navy} />
      </svg>
    </div>
  );
};

// Rule Card component for Rules of Thumb section
const RuleCard = ({ rule, index, isVisible }) => {
  const Icon = rule.icon;
  return (
    <div 
      className="p-4 sm:p-6 rounded-2xl"
      style={{ 
        backgroundColor: '#FFFFFF',
        border: '1px solid rgba(0,31,63,0.08)',
        boxShadow: '0 2px 8px rgba(0,31,63,0.04), 0 1px 3px rgba(0,0,0,0.02)',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(12px)',
        transition: `opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.05}s, transform 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.05}s`,
      }}
    >
      {/* Icon */}
      <div 
        className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
        style={{ 
          background: rule.iconBg,
        }}
      >
        <Icon 
          className="w-5 h-5" 
          style={{ color: rule.iconColor }} 
          strokeWidth={1.5} 
        />
      </div>
      
      {/* Headline */}
      <h4 
        className="font-semibold mb-2"
        style={{ 
          color: BRAND.navy,
          fontSize: '1rem',
          lineHeight: '1.4',
        }}
      >
        {rule.title}
      </h4>
      
      {/* Body text */}
      <p 
        className="leading-relaxed"
        style={{ 
          color: '#6B7280',
          fontSize: '0.875rem',
          lineHeight: '1.6',
        }}
      >
        {rule.content}
      </p>
    </div>
  );
};

// Always-visible Rules of Thumb Section (no accordion)
const RulesOfThumbStatic = () => {
  const rules = [
    {
      title: 'The 10% guideline',
      content: "Many educators suggest keeping your car payment under 10% of take-home pay. This is not a hard rule, but it helps leave room for insurance, fuel, and the rest of life. If your payment is under control, you'll have more room for insurance, fuel, and surprises.",
      icon: PiggyBank,
      iconBg: 'linear-gradient(135deg, #001F3F 0%, #2A52A0 100%)',
      iconColor: '#FFFFFF',
    },
    {
      title: 'Total cost of ownership',
      content: 'The payment is not the whole picture. Insurance, fuel, maintenance, parking, and tolls can add often a few hundred dollars a month. Factor these in before deciding what payment fits. A "cheap payment" can still be an expensive car.',
      icon: Calculator,
      iconBg: 'linear-gradient(135deg, #001F3F 0%, #2A52A0 100%)',
      iconColor: '#FFFFFF',
    },
    {
      title: 'Leave breathing room',
      content: 'A budget that is too tight turns small surprises into debt. A conservative buffer helps you stay ahead instead of falling behind when things happen. The buffer is what keeps a normal repair from becoming debt.',
      icon: Wind,
      iconBg: 'linear-gradient(135deg, #001F3F 0%, #2A52A0 100%)',
      iconColor: '#FFFFFF',
    },
    {
      title: 'Insurance can change everything',
      content: 'Insurance rates vary widely by car, driver, and location. Get a quick quote before committing to a payment that looks affordable on paper. A quick quote can prevent a payment surprise after you fall in love with the car.',
      icon: Umbrella,
      iconBg: 'linear-gradient(135deg, #001F3F 0%, #2A52A0 100%)',
      iconColor: '#FFFFFF',
    },
  ];

  return (
    <div>
      <h3 
        className="text-base font-bold mb-5" 
        style={{ color: BRAND.navy }}
      >
        Helpful budgeting guidelines
      </h3>
      {/* 2-column grid on desktop, 1-column on mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        {rules.map((rule, index) => (
          <RuleCard 
            key={index} 
            rule={rule} 
            index={index} 
            isVisible={true}
          />
        ))}
      </div>
    </div>
  );
};

const BudgetCheckStep = ({ onContinue, onSkip, onBack }) => {
  const [formData, setFormData] = useState({
    monthlyIncome: 5000,
    housing: 1500,      // ~30% of $5000
    essentials: 850,    // ~17% of $5000
    debtPayments: 400,  // ~8% of $5000
    savingsGoal: 500,   // ~10% of $5000
    wants: 350,         // ~7% of $5000
    insurance: 160,     // Midpoint estimate
    fuel: 175,          // Midpoint estimate
    maintenance: 80,    // Midpoint estimate
  });
  
  const [inputText, setInputText] = useState({});
  const [riskLevel, setRiskLevel] = useState('balanced');
  const [apr, setApr] = useState('');
  const [term, setTerm] = useState('60');
  const [result, setResult] = useState(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  
  // Track which field is currently being edited and store refs to inputs
  const editingFieldRef = useRef(null);
  const inputRefs = useRef({});

  // Subtle slider nudge animation on page load
  useEffect(() => {
    if (!hasAnimated) {
      const timer = setTimeout(() => {
        // Briefly nudge the income slider to show interactivity
        setFormData(prev => ({ ...prev, monthlyIncome: 5150 }));
        setTimeout(() => {
          setFormData(prev => ({ ...prev, monthlyIncome: 5000 }));
          setHasAnimated(true);
        }, 450);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [hasAnimated]);

  const formatCurrency = (num) => {
    if (isNaN(num) || num === null || num === undefined) return '$0';
    return '$' + Math.round(num).toLocaleString('en-US');
  };

  // Track if we're programmatically updating (vs user interaction)
  const isUserTypingRef = useRef(false);
  
  const handleSliderChange = useCallback((field, values) => {
    // Ignore slider changes that come from programmatic updates while user is typing
    if (isUserTypingRef.current) {
      return;
    }
    
    const value = values[0];
    setFormData(prev => ({ ...prev, [field]: value }));
    // Update the input element directly
    if (inputRefs.current[field]) {
      inputRefs.current[field].value = value.toString();
    }
    setInputText(prev => ({ ...prev, [field]: undefined }));
    setResult(null);
  }, []);

  const handleInputFocus = useCallback((field) => {
    editingFieldRef.current = field;
    isUserTypingRef.current = true;
  }, []);

  const handleInputChange = useCallback((field, textValue) => {
    // Parse the numeric value - remove non-digits
    const cleaned = textValue.replace(/[^0-9]/g, '');
    const numValue = parseInt(cleaned, 10);
    
    if (!isNaN(numValue) && numValue >= 0) {
      const config = SLIDER_CONFIG[field];
      // Clamp to valid range for the slider
      const clampedValue = Math.min(Math.max(numValue, config.min), config.max);
      setFormData(prev => ({ ...prev, [field]: clampedValue }));
    } else if (cleaned === '' || textValue === '') {
      setFormData(prev => ({ ...prev, [field]: 0 }));
    }
    setResult(null);
  }, []);

  const handleInputBlur = useCallback((field, e) => {
    // Mark that we're no longer editing
    editingFieldRef.current = null;
    isUserTypingRef.current = false;
    
    // Normalize the displayed value on blur
    const cleaned = (e.target.value || '').replace(/[^0-9]/g, '');
    const numValue = parseInt(cleaned, 10) || 0;
    const config = SLIDER_CONFIG[field];
    const clampedValue = Math.min(Math.max(numValue, config.min), config.max);
    
    // Update the input to show the normalized value
    e.target.value = clampedValue.toString();
    setFormData(prev => ({ ...prev, [field]: clampedValue }));
    setInputText(prev => ({ ...prev, [field]: undefined }));
  }, []);

  const getDisplayValue = (field) => {
    // This is used for initial render - actual value is managed by the input ref
    return formData[field].toString();
  };

  const calculate = () => {
    const income = formData.monthlyIncome || 0;
    if (income <= 0) {
      setResult({ error: 'Please enter your monthly take-home income.' });
      return;
    }

    const mustPay = (formData.housing || 0) + (formData.essentials || 0) + (formData.debtPayments || 0);
    const goalsFlex = (formData.savingsGoal || 0) + (formData.wants || 0);
    const runningCosts = (formData.insurance || 0) + (formData.fuel || 0) + (formData.maintenance || 0);

    const { incomePct, bufferPct } = RISK_CONFIG[riskLevel];
    const buffer = income * bufferPct;
    const leftover = income - mustPay - goalsFlex - buffer;
    const incomeCap = income * incomePct;
    const maxAllIn = Math.max(0, Math.min(incomeCap, leftover));
    const maxPayment = Math.max(0, maxAllIn - runningCosts);
    const allInCarCost = maxPayment + runningCosts;

    // Price estimate if APR provided
    let maxPrice = null;
    const aprNum = parseFloat(apr);
    const termNum = parseInt(term, 10);
    if (!isNaN(aprNum) && aprNum > 0 && !isNaN(termNum) && termNum > 0) {
      const monthlyRate = aprNum / 100 / 12;
      maxPrice = maxPayment * (1 - Math.pow(1 + monthlyRate, -termNum)) / monthlyRate;
    }

    setResult({
      income,
      mustPay,
      goalsFlex,
      runningCosts,
      buffer,
      leftover,
      incomeCap,
      maxAllIn,
      maxPayment,
      allInCarCost,
      maxPrice,
      riskLevel,
      isTight: leftover < 0,
      noRoom: maxAllIn === 0,
    });
  };

  // Slider row component with tap-to-type pattern
  const SliderRow = ({ field, required = false, iconBg }) => {
    const Icon = FIELD_ICONS[field];
    const config = SLIDER_CONFIG[field];
    const value = formData[field];
    const bgStyle = iconBg || ICON_BACKGROUNDS.fixed;
    const placeholder = FIELD_PLACEHOLDERS[field];
    const tooltip = FIELD_TOOLTIPS[field];
    
    // Calculate fill percentage for the slider track
    const percent = ((value - config.min) / (config.max - config.min)) * 100;
    
    // Local state for editing
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    
    const handleValueClick = () => {
      setIsEditing(true);
      setEditValue(value.toString());
    };
    
    const handleInputChange = (e) => {
      setEditValue(e.target.value);
    };
    
    const handleInputBlur = () => {
      const raw = editValue.replace(/[^0-9]/g, '');
      const num = parseInt(raw, 10);
      if (!isNaN(num) && num >= 0) {
        const clamped = Math.min(Math.max(num, config.min), config.max);
        setFormData(prev => ({ ...prev, [field]: clamped }));
        setResult(null);
      }
      setIsEditing(false);
    };
    
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        handleInputBlur();
      } else if (e.key === 'Escape') {
        setIsEditing(false);
      }
    };
    
    return (
      <div className="py-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-transform hover:scale-105"
            style={{ background: bgStyle.bg }}
          >
            <Icon className="w-4 h-4" style={{ color: bgStyle.color }} strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div style={{ marginBottom: '20px' }}>
              {/* Row 1: Label left, tap-to-type value right */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: 600, color: '#1F2937' }}>
                  {FIELD_LABELS[field]}
                  {required && <span style={{ color: BRAND.red, marginLeft: '2px' }}>*</span>}
                  {tooltip && (
                    <span className="ml-1 cursor-help" title={tooltip} style={{ color: '#9CA3AF' }}>
                      <Info className="w-3.5 h-3.5 inline" strokeWidth={2} />
                    </span>
                  )}
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    value={editValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    className="slider-tap-value"
                  />
                ) : (
                  <span
                    onClick={handleValueClick}
                    className="slider-tap-value"
                    style={{ cursor: 'text' }}
                  >
                    ${value.toLocaleString()}
                  </span>
                )}
              </div>
              {/* Row 2: Full width slider */}
              <input
                type="range"
                min={config.min}
                max={config.max}
                step={config.step}
                value={value}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, [field]: Number(e.target.value) }));
                  setResult(null);
                }}
                className="budget-slider"
                style={{
                  width: '100%',
                  height: '6px',
                  cursor: 'pointer',
                  background: `linear-gradient(to right, #001F3F 0%, #001F3F ${percent}%, #E5E7EB ${percent}%, #E5E7EB 100%)`,
                }}
              />
            </div>
            <p className="text-xs" style={{ color: '#9CA3AF', marginTop: '-12px' }}>{FIELD_HELPERS[field]}</p>
            {placeholder && value === 0 && (
              <p className="text-xs mt-1" style={{ color: '#94A3B8', fontStyle: 'italic' }}>{placeholder}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen pb-32" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(to bottom, #FFFFFF 0%, #FAFBFC 100%)',
        borderBottom: '1px solid #E8EAED',
      }}>
        <div className="max-w-screen-sm mx-auto px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {onBack && (
                <button onClick={onBack} className="p-1 -ml-2" style={{ color: BRAND.navy }}>
                  <ChevronLeft className="w-6 h-6" strokeWidth={2} />
                </button>
              )}
              <Wallet className="w-5 h-5" style={{ color: BRAND.navy }} strokeWidth={2} />
              <h1 className="text-lg font-bold" style={{ color: BRAND.navy }}>Budget check</h1>
            </div>
            <div className="flex items-baseline gap-2">
              <button
                onClick={onSkip}
                className="text-sm font-semibold hover:underline"
                style={{ color: BRAND.navy }}
              >
                Skip
              </button>
              <span className="text-sm font-semibold" style={{ color: BRAND.navy }}>
                VINdicated.<span style={{ color: BRAND.red }}>ai</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section - Enhanced with radial tint */}
      <div style={{ 
        background: 'radial-gradient(ellipse at top center, rgba(0,31,63,0.03) 0%, rgba(248,249,251,0) 70%)',
      }}>
        <div className="max-w-screen-sm mx-auto px-5 pt-6 pb-5">
          {/* Premium Hero Card - Elevated with enhanced shadow */}
          <div 
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '24px',
              boxShadow: '0 4px 24px rgba(0,31,63,0.08), 0 1px 3px rgba(0,31,63,0.04)',
              border: '1px solid rgba(0,31,63,0.04)',
              padding: '28px',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FAFCFF 100%)',
            }}
          >
            {/* Mobile Layout (stacked) */}
            <div className="block sm:hidden">
              {/* Copy first on mobile */}
              <h2 
                className="font-bold text-center" 
                style={{ 
                  color: BRAND.navy,
                  fontSize: '20px',
                  lineHeight: '1.3',
                  marginBottom: '14px',
                }}
              >
                Define your needs and set a budget you can comfortably afford.
              </h2>
              <p 
                className="text-center"
                style={{ 
                  color: '#1F2937',
                  fontSize: '14px',
                  lineHeight: '1.65',
                  marginBottom: '10px',
                  maxWidth: '340px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                Most people buy cars in the wrong order. This step helps you set guardrails first — before pricing, financing, or pressure enter the picture.
              </p>
              <p 
                className="text-center"
                style={{ 
                  color: '#1F2937',
                  fontSize: '14px',
                  lineHeight: '1.65',
                  maxWidth: '340px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                Already know your number? You can skip this step. If not, this takes about two minutes and helps prevent overcommitting.
              </p>
              {/* Gauge visual below copy on mobile */}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <BudgetGauge size="mobile" />
              </div>
              {/* Helper lines inside card on mobile */}
              <div 
                style={{ 
                  marginTop: '20px',
                  paddingTop: '18px',
                  borderTop: '1px solid rgba(0,31,63,0.06)',
                }}
              >
                <p 
                  className="text-center"
                  style={{ 
                    color: '#374151',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    fontWeight: '500',
                  }}
                >
                  In about two minutes, you'll see a safe payment range and a comfort check.
                </p>
              </div>
            </div>

            {/* Desktop Layout (2-column) */}
            <div 
              className="hidden sm:flex"
              style={{ 
                alignItems: 'center',
                gap: '32px',
              }}
            >
              {/* Left: Copy */}
              <div style={{ flex: '1 1 55%' }}>
                <h2 
                  className="font-bold" 
                  style={{ 
                    color: BRAND.navy,
                    fontSize: '22px',
                    lineHeight: '1.3',
                    marginBottom: '14px',
                  }}
                >
                  Define your needs and set a budget you can comfortably afford.
                </h2>
                <p 
                  style={{ 
                    color: '#1F2937',
                    fontSize: '14px',
                    lineHeight: '1.65',
                    marginBottom: '10px',
                    maxWidth: '380px',
                  }}
                >
                  Most people buy cars in the wrong order. This step helps you set guardrails first — before pricing, financing, or pressure enter the picture.
                </p>
                <p 
                  style={{ 
                    color: '#1F2937',
                    fontSize: '14px',
                    lineHeight: '1.65',
                    maxWidth: '380px',
                  }}
                >
                  Already know your number? You can skip this step. If not, this takes about two minutes and helps prevent overcommitting.
                </p>
              </div>
              {/* Right: Premium Gauge Visual */}
              <div style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <BudgetGauge size="desktop" />
              </div>
            </div>

            {/* Helper lines inside the card */}
            <div 
              style={{ 
                marginTop: '20px',
                paddingTop: '18px',
                borderTop: '1px solid rgba(0,31,63,0.06)',
              }}
            >
              <p 
                style={{ 
                  color: '#374151',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  textAlign: 'center',
                  fontWeight: '500',
                }}
              >
                In about two minutes, you'll see a safe payment range and a comfort check.
              </p>
              <p 
                style={{ 
                  color: '#6B7280',
                  fontSize: '12px',
                  lineHeight: '1.5',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                This quick check helps you avoid one of the most common causes of buyer's remorse — buying more car than your budget can comfortably support.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Rules of Thumb Section - Always visible */}
      <div className="max-w-screen-sm mx-auto px-5 pt-5 pb-6">
        <RulesOfThumbStatic />
      </div>

      {/* Results and remaining content */}
      <div className="max-w-screen-sm mx-auto px-5 pb-6" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Results */}
        {result && !result.error && (
          <>
            <div 
              className="p-5 relative overflow-hidden"
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '20px',
                boxShadow: '0 4px 20px rgba(0,31,63,0.08)',
                border: `2px solid ${BRAND.navy}`,
              }}
            >
              <div 
                className="absolute top-0 left-0 right-0 h-1.5"
                style={{ background: `linear-gradient(90deg, ${BRAND.navy} 0%, #4A6FA5 100%)` }}
              />
              
              {result.isTight && (
                <div className="mb-4 p-3 rounded-lg flex items-start gap-2" style={{ backgroundColor: `${BRAND.amber}10` }}>
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: BRAND.amber }} />
                  <p className="text-xs" style={{ color: '#92400E' }}>
                    Your budget is tight. Consider lowering expenses, increasing income, or delaying.
                  </p>
                </div>
              )}

              {result.noRoom && !result.isTight && (
                <div className="mb-4 p-3 rounded-lg flex items-start gap-2" style={{ backgroundColor: `${BRAND.amber}10` }}>
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: BRAND.amber }} />
                  <p className="text-xs" style={{ color: '#92400E' }}>
                    Based on what you entered, there may not be room for a car payment right now without changing other spending.
                  </p>
                </div>
              )}

              <div className="text-center pt-2 mb-4">
                <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>
                  Suggested max payment ({RISK_CONFIG[result.riskLevel].label})
                </p>
                <p className="text-4xl font-black" style={{ color: BRAND.navy }}>
                  {formatCurrency(result.maxPayment)}
                  <span className="text-lg font-medium ml-1" style={{ color: '#6B7280' }}>/mo</span>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-4" style={{ borderTop: '1px solid #E8EAED' }}>
                <div className="text-center p-3 rounded-xl" style={{ backgroundColor: '#FAFBFC' }}>
                  <p className="text-xs mb-1" style={{ color: '#6B7280' }}>All-in car cost</p>
                  <p className="text-lg font-bold" style={{ color: '#374151' }}>
                    {formatCurrency(result.allInCarCost)}<span className="text-xs font-normal">/mo</span>
                  </p>
                </div>
                <div className="text-center p-3 rounded-xl" style={{ backgroundColor: '#FAFBFC' }}>
                  <p className="text-xs mb-1" style={{ color: '#6B7280' }}>Running costs</p>
                  <p className="text-lg font-bold" style={{ color: '#374151' }}>
                    {formatCurrency(result.runningCosts)}<span className="text-xs font-normal">/mo</span>
                  </p>
                </div>
              </div>

              {result.maxPrice !== null && (
                <div className="mt-4 p-3 rounded-xl" style={{ backgroundColor: '#FAFBFC' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: BRAND.navy }}>Estimated max price</p>
                  <p className="text-xl font-bold" style={{ color: '#374151' }}>{formatCurrency(result.maxPrice)}</p>
                  <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>Rough estimate. Excludes taxes, fees, down payment.</p>
                </div>
              )}
            </div>

            <button
              onClick={() => setResult(null)}
              className="w-full py-3 text-sm font-semibold transition-all hover:bg-gray-50 active:scale-[0.98]"
              style={{
                backgroundColor: '#FFFFFF',
                color: '#4B5563',
                borderRadius: '14px',
                border: '2px solid #D1D5DB',
              }}
            >
              Adjust inputs
            </button>
          </>
        )}

        {result && result.error && (
          <div className="p-4 flex items-center gap-3" style={{ backgroundColor: '#FEF2F2', borderRadius: '14px' }}>
            <AlertTriangle className="w-5 h-5" style={{ color: '#DC2626' }} />
            <p className="text-sm font-medium" style={{ color: '#DC2626' }}>{result.error}</p>
          </div>
        )}

        {/* Form */}
        {!result && (
          <>
            {/* Income intro text */}
            <p className="text-sm text-center mb-4" style={{ color: '#4B5563' }}>
              Start with your monthly income and we'll do the math.
            </p>

            {/* Income - Enhanced section */}
            <div 
              className="p-5 mt-2"
              style={{ 
                backgroundColor: '#FFFFFF', 
                borderRadius: '20px', 
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 8px rgba(0,31,63,0.03)',
              }}
            >
              <SliderRow field="monthlyIncome" required iconBg={ICON_BACKGROUNDS.income} />
            </div>
            <p className="text-xs px-2 -mt-3" style={{ color: '#9CA3AF' }}>
              Try adjusting the sliders or typing exact amounts.
            </p>

            {/* Fixed Commitments - Enhanced with more spacing */}
            <div 
              className="p-5 mt-4"
              style={{ 
                backgroundColor: '#FFFFFF', 
                borderRadius: '20px', 
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 8px rgba(0,31,63,0.03)',
              }}
            >
              <h4 className="text-xs font-semibold uppercase tracking-wider mb-1 px-1" style={{ color: '#9CA3AF' }}>Fixed commitments</h4>
              <SliderRow field="housing" iconBg={ICON_BACKGROUNDS.fixed} />
              <SliderRow field="essentials" iconBg={ICON_BACKGROUNDS.fixed} />
              <SliderRow field="debtPayments" iconBg={ICON_BACKGROUNDS.fixed} />
            </div>

            {/* Live Preview - Remaining after fixed commitments */}
            <div 
              className="p-4 mt-3"
              style={{ 
                backgroundColor: '#F8FAFC', 
                borderRadius: '14px', 
                border: '1px solid #E8EAED',
              }}
            >
              <p className="text-sm text-center" style={{ color: '#4B5563' }}>
                Remaining after fixed commitments:{' '}
                <span className="font-bold" style={{ color: BRAND.navy }}>
                  {formatCurrency(formData.monthlyIncome - formData.housing - formData.essentials - formData.debtPayments)}
                </span>
                {' '}available for goals, fun money, your car, and surprises.
              </p>
            </div>

            {/* Goals & Flex - Enhanced */}
            <div 
              className="p-5 mt-4"
              style={{ 
                backgroundColor: '#FFFFFF', 
                borderRadius: '20px', 
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 8px rgba(0,31,63,0.03)',
              }}
            >
              <h4 className="text-xs font-semibold uppercase tracking-wider mb-1 px-1" style={{ color: '#9CA3AF' }}>Goals & flexible spending</h4>
              <SliderRow field="savingsGoal" iconBg={ICON_BACKGROUNDS.goals} />
              <SliderRow field="wants" iconBg={ICON_BACKGROUNDS.goals} />
            </div>

            {/* Car Costs - Enhanced */}
            <div 
              className="p-5 mt-4"
              style={{ 
                backgroundColor: '#FFFFFF', 
                borderRadius: '20px', 
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 8px rgba(0,31,63,0.03)',
              }}
            >
              <h4 className="text-xs font-semibold uppercase tracking-wider mb-1 px-1" style={{ color: '#9CA3AF' }}>Estimated car costs (optional)</h4>
              <SliderRow field="insurance" iconBg={ICON_BACKGROUNDS.car} />
              <SliderRow field="fuel" iconBg={ICON_BACKGROUNDS.car} />
              <SliderRow field="maintenance" iconBg={ICON_BACKGROUNDS.car} />
            </div>

            {/* Risk Tolerance - Enhanced with inset selected state */}
            <div 
              className="p-5 mt-4"
              style={{ 
                backgroundColor: '#FFFFFF', 
                borderRadius: '20px', 
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 8px rgba(0,31,63,0.03)',
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div 
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #1F2937, #4B5563)' }}
                >
                  <Scale className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                </div>
                <span className="text-sm font-semibold" style={{ color: '#374151' }}>Risk tolerance</span>
              </div>
              <div className="flex gap-2">
                {Object.entries(RISK_CONFIG).map(([key, { label }]) => (
                  <button
                    key={key}
                    onClick={() => setRiskLevel(key)}
                    className="flex-1 py-3 text-sm font-medium rounded-xl transition-all active:scale-[0.97]"
                    style={{
                      backgroundColor: riskLevel === key ? BRAND.navy : '#F5F6F8',
                      color: riskLevel === key ? '#FFFFFF' : '#4B5563',
                      boxShadow: riskLevel === key 
                        ? 'inset 0 2px 4px rgba(0,0,0,0.15), 0 2px 8px rgba(0,31,63,0.2)' 
                        : 'none',
                      border: riskLevel === key ? 'none' : '1px solid #E8EAED',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {riskLevel === 'balanced' && (
                <p className="text-xs text-center mt-3" style={{ color: '#6B7280' }}>
                  Balanced works well for most buyers.
                </p>
              )}
            </div>

            {/* APR Section - Enhanced */}
            <div 
              className="p-5 mt-4"
              style={{ 
                backgroundColor: '#FFFFFF', 
                borderRadius: '20px', 
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 8px rgba(0,31,63,0.03)',
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div 
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #001F3F, #2A52A0)' }}
                >
                  <Percent className="w-4 h-4" style={{ color: '#FFFFFF' }} strokeWidth={2} />
                </div>
                <span className="text-sm font-semibold" style={{ color: '#374151' }}>Optional: Estimate affordable vehicle price</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B7280' }}>Example APR (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={apr}
                    onChange={(e) => setApr(e.target.value.replace(/[^0-9.]/g, ''))}
                    placeholder="e.g. 7"
                    className="w-full px-3 py-3 text-sm rounded-xl transition-all focus:ring-2 focus:ring-blue-100"
                    style={{ border: '1px solid #E5E7EB', backgroundColor: '#FAFBFC', color: BRAND.navy }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B7280' }}>Term (months)</label>
                  <select
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    className="w-full px-3 py-3 text-sm rounded-xl transition-all focus:ring-2 focus:ring-blue-100"
                    style={{ border: '1px solid #E5E7EB', backgroundColor: '#FAFBFC', color: BRAND.navy }}
                  >
                    <option value="48">48 mo</option>
                    <option value="60">60 mo</option>
                    <option value="72">72 mo</option>
                  </select>
                </div>
              </div>
              <p className="text-xs mt-3 leading-relaxed" style={{ color: '#6B7280' }}>
                Enter an example APR (e.g., 7%) and loan term (e.g., 60 months) to see a rough estimate of the vehicle price that fits your safe monthly payment. This works backward from your budget — it shows what car price keeps payments affordable at those rates/terms. Actual rates vary by lender, credit profile, market conditions, and promotions (including occasional 0% offers); this is just illustrative.
              </p>
            </div>

            {/* Buttons - Reduced spacing, hover animations */}
            <div className="space-y-2 pt-3">
              <button
                onClick={calculate}
                className="w-full py-4 text-base font-bold flex items-center justify-center gap-2 transition-all hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
                style={{
                  backgroundColor: BRAND.navy,
                  color: '#FFFFFF',
                  borderRadius: '14px',
                  boxShadow: '0 4px 12px rgba(0,31,63,0.2)',
                }}
              >
                <span>Find my safe car payment</span>
                <ArrowRight className="w-5 h-5" style={{ color: BRAND.red }} strokeWidth={2.5} />
              </button>
            </div>
          </>
        )}

        {/* Privacy note moved to bottom */}
        <p 
          className="text-center"
          style={{ 
            color: '#9CA3AF',
            fontSize: '12px',
            lineHeight: '1.5',
            paddingTop: '12px',
          }}
        >
          Private by design — no credit check or account connection required. The numbers you enter are not stored, saved, or shared.
        </p>

        {/* Disclaimer */}
        <div className="flex items-center justify-center gap-2 pt-2">
          <Info className="w-3.5 h-3.5" style={{ color: '#9CA3AF' }} />
          <p className="text-xs" style={{ color: '#9CA3AF' }}>
            Education only. Not financial, legal, or tax advice.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BudgetCheckStep;
